const normalizeIdentity = (value) => (value == null ? '' : String(value).trim().toLowerCase());

const addIdentity = (set, value) => {
  const normalized = normalizeIdentity(value);
  if (normalized) set.add(normalized);
};

/** Organiser/customer identities for a thread (guest email preferred over mis-linked customer_id). */
export const collectOrganizerIdentities = (thread) => {
  const userIds = new Set();
  const emails = new Set();

  const guestEmail = normalizeIdentity(
    thread?.guest_organizer_email ||
      thread?.guestOrganizerEmail ||
      thread?.requirements?.customer_info?.email
  );
  const linkedCustomerId = normalizeIdentity(thread?.customer_id || thread?.customerId);
  const linkedCustomerEmail = normalizeIdentity(thread?.customer_email || thread?.customerEmail);

  if (guestEmail) {
    addIdentity(emails, guestEmail);
    if (linkedCustomerId && linkedCustomerEmail && guestEmail === linkedCustomerEmail) {
      addIdentity(userIds, linkedCustomerId);
    }
  } else {
    addIdentity(emails, thread?.organizer_email || thread?.organizerEmail);
    addIdentity(emails, thread?.customer_email || thread?.customerEmail);
    if (linkedCustomerId) {
      addIdentity(userIds, linkedCustomerId);
    }
  }

  addIdentity(userIds, thread?.event_organizer_id || thread?.eventOrganizerId);
  addIdentity(userIds, thread?.organizer_id || thread?.organizerId);

  if (Array.isArray(thread?.participants)) {
    thread.participants.forEach((participant) => {
      if (String(participant?.role || '').toLowerCase() !== 'organiser') return;
      const participantId = normalizeIdentity(participant?.user_id || participant?.id);
      const participantEmail = normalizeIdentity(participant?.email);
      if (guestEmail) {
        if (participantEmail && participantEmail === guestEmail) {
          addIdentity(emails, participantEmail);
        }
        if (
          participantId &&
          linkedCustomerId &&
          participantId === linkedCustomerId &&
          guestEmail === linkedCustomerEmail
        ) {
          addIdentity(userIds, participantId);
        }
      } else {
        addIdentity(userIds, participant?.user_id || participant?.id);
        addIdentity(emails, participant?.email);
      }
    });
  }

  return { userIds, emails };
};

/** True when the signed-in user is the organiser/customer for this thread. */
export const isActorOrganizerForThread = (thread, actorUserId, actorEmail) => {
  const { userIds, emails } = collectOrganizerIdentities(thread);
  const actorId = normalizeIdentity(actorUserId);
  const actorEmailNorm = normalizeIdentity(actorEmail);
  return (
    (actorId && userIds.has(actorId)) ||
    (actorEmailNorm && emails.has(actorEmailNorm))
  );
};

const senderMatchesOrganizer = (message, organizerIdentities) => {
  const senderId = normalizeIdentity(message?.sender_id);
  const senderEmail = normalizeIdentity(message?.sender_email);
  return (
    (senderId && organizerIdentities.userIds.has(senderId)) ||
    (senderEmail && organizerIdentities.emails.has(senderEmail))
  );
};

/**
 * True when the bubble should appear on the right (sent by the current viewer).
 * @param {'venue'|'organizer'} viewerContext
 */
export const isMessageSentByViewer = (
  message,
  { actorUserId, actorEmail, viewerContext = 'organizer', thread = null, venueOwnerId = null } = {}
) => {
  const senderId = normalizeIdentity(message?.sender_id);
  const senderEmail = normalizeIdentity(message?.sender_email);
  const actorId = normalizeIdentity(actorUserId);
  const actorEmailNorm = normalizeIdentity(actorEmail);
  const venueOwnerIdNorm = normalizeIdentity(venueOwnerId);

  if (senderId && actorId && senderId === actorId) return true;
  if (senderEmail && actorEmailNorm && senderEmail === actorEmailNorm) return true;

  const organizerIdentities = thread ? collectOrganizerIdentities(thread) : { userIds: new Set(), emails: new Set() };
  const senderIsOrganizer = thread ? senderMatchesOrganizer(message, organizerIdentities) : false;
  const senderIsVenueOwner = venueOwnerIdNorm && senderId && senderId === venueOwnerIdNorm;

  if (viewerContext === 'venue') {
    if (senderIsVenueOwner) return true;
    if (venueOwnerIdNorm && actorId === venueOwnerIdNorm && !senderIsOrganizer) {
      return true;
    }
    return false;
  }

  if (senderIsOrganizer) {
    return Boolean(
      (senderId && actorId && senderId === actorId) ||
        (senderEmail && actorEmailNorm && senderEmail === actorEmailNorm)
    );
  }

  return false;
};

export { normalizeIdentity };

/** Guest organiser email from a booking API row (requirements.customer_info is authoritative for manual bookings). */
export const guestEmailFromBooking = (booking) => {
  if (!booking || typeof booking !== 'object') return null;
  const requirements = booking.requirements || booking.requirements_json;
  let req = requirements;
  if (typeof requirements === 'string') {
    try {
      req = JSON.parse(requirements);
    } catch {
      req = {};
    }
  }
  const fromReq = (req?.customer_info?.email || '').trim();
  const fromContact = (booking.contact_email || booking.contactEmail || '').trim();
  const fromCustomer = (booking.customer_email || booking.customerEmail || '').trim();
  const email = fromReq || fromContact || fromCustomer;
  return email ? normalizeIdentity(email) : null;
};

export const bookingContactToThreadMeta = (booking) => {
  const guestEmail = guestEmailFromBooking(booking);
  if (!guestEmail) return null;
  return {
    guest_organizer_email: guestEmail,
    customer_id: booking.customer_id || booking.customerId || null,
    customer_email: guestEmail
  };
};
